<template>
  <div>
    <div class="d-flex flex-wrap align-items-end justify-content-between mb-2 gap-1">
      <div class="d-flex flex-wrap align-items-end gap-1">
        <div class="mr-1">
          <label class="d-block mb-50">Időszak</label>
          <b-form-input
            v-model="period"
            type="month"
          />
        </div>
        <b-button
          variant="primary"
          class="mt-1"
          @click="loadProjectDetails"
        >
          Frissítés
        </b-button>
      </div>
      <b-button
        variant="outline-secondary"
        class="mt-1"
        @click="goBack"
      >
        Vissza a dashboardhoz
      </b-button>
    </div>

    <loading :status="loading" />

    <div v-if="!loading && projectData">
      <b-card class="mb-2">
        <h4 class="mb-1">
          {{ projectData.project.name }}
        </h4>

        <b-row>
          <b-col
            cols="12"
            md="4"
            class="mb-1"
          >
            <div class="text-muted mb-25">
              Munka azonosító
            </div>
            <div class="font-weight-bold">
              {{ projectData.project.work_number || '-' }}
            </div>
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mb-1"
          >
            <div class="text-muted mb-25">
              Kezdés dátuma
            </div>
            <div class="font-weight-bold">
              {{ formatDate(projectData.project.start_date) }}
            </div>
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mb-1"
          >
            <div class="text-muted mb-25">
              Befejezés dátuma
            </div>
            <div class="font-weight-bold">
              {{ formatDate(projectData.project.end_date) }}
            </div>
          </b-col>
        </b-row>

        <hr>

        <div class="d-flex justify-content-between align-items-center mb-50">
          <div class="text-muted">
            Időalapú projekt előrehaladás
          </div>
          <div class="font-weight-bold">
            {{ timelineProgressLabel }}
          </div>
        </div>
        <b-progress
          :value="timelineProgress"
          max="100"
          height="22px"
          variant="success"
        />
      </b-card>

      <b-card>
        <h4 class="mb-1">
          {{ projectData.project.name }} - részletek
        </h4>
        <p class="text-muted">
          {{ projectData.risk ? (projectData.risk.ai_summary || projectData.risk.summary) : 'Nincs kockázati összefoglaló.' }}
        </p>

        <b-row v-if="projectData.risk && (projectData.risk.reasons || projectData.risk.actions)">
          <b-col
            cols="12"
            lg="6"
            class="mb-1"
          >
            <b-card
              bg-variant="transparent"
              border-variant="secondary"
              class="h-100"
            >
              <h5 class="mb-1">
                Fő okok
              </h5>
              <ul class="mb-0 pl-1">
                <li
                  v-for="(reason, idx) in (projectData.risk.reasons || [])"
                  :key="`reason-${idx}`"
                >
                  {{ reason }}
                </li>
              </ul>
            </b-card>
          </b-col>
          <b-col
            cols="12"
            lg="6"
            class="mb-1"
          >
            <b-card
              bg-variant="transparent"
              border-variant="secondary"
              class="h-100"
            >
              <h5 class="mb-1">
                Javasolt teendők (7 nap)
              </h5>
              <ul class="mb-0 pl-1">
                <li
                  v-for="(action, idx) in (projectData.risk.actions || [])"
                  :key="`action-${idx}`"
                >
                  {{ action }}
                </li>
              </ul>
            </b-card>
          </b-col>
        </b-row>

        <b-table
          :items="projectData.variances || []"
          :fields="varianceFields"
          striped
          responsive
          small
        >
          <template #cell(variance_key)="data">
            {{ varianceLabel(data.item.variance_key) }}
          </template>
          <template #cell(planned_value)="data">
            {{ money(data.item.planned_value) }}
          </template>
          <template #cell(actual_value)="data">
            {{ money(data.item.actual_value) }}
          </template>
          <template #cell(variance_value)="data">
            {{ money(data.item.variance_value) }}
          </template>
          <template #cell(variance_percent)="data">
            {{ Number(data.item.variance_percent || 0).toFixed(2) }}%
          </template>
        </b-table>
      </b-card>

      <b-card class="mt-2">
        <h4 class="mb-1">
          Adatforrás összefoglaló
        </h4>
        <b-table
          :items="sourceSummaryRows"
          :fields="sourceFields"
          striped
          responsive
          small
        />
      </b-card>

      <b-card class="mt-2">
        <h4 class="mb-1">
          Előkészítés terv adatok (projekt kártya)
        </h4>
        <p class="text-muted mb-1">
          Forrás: Előkészítés / projekt kártya (import vagy kézi rögzítés).
        </p>

        <div v-if="!preparationPlan.has_data" class="text-muted">
          Ehhez a projekthez nincs előkészítés terv adat.
        </div>

        <div v-else>
          <b-table
            :items="preparationPlan.rows || []"
            :fields="preparationPlanFields"
            striped
            responsive
            small
            class="mb-1"
          >
            <template #cell(material_total)="data">
              {{ money(data.item.material_total) }}
            </template>
            <template #cell(fee_total)="data">
              {{ money(data.item.fee_total) }}
            </template>
            <template #cell(net_total)="data">
              {{ money(data.item.net_total) }}
            </template>
          </b-table>

          <b-row>
            <b-col cols="12" md="4" class="mb-1">
              <div class="text-muted mb-25">Anyag összesen</div>
              <div class="font-weight-bold">{{ money(preparationPlan.totals.material_total) }}</div>
            </b-col>
            <b-col cols="12" md="4" class="mb-1">
              <div class="text-muted mb-25">Díj összesen</div>
              <div class="font-weight-bold">{{ money(preparationPlan.totals.fee_total) }}</div>
            </b-col>
            <b-col cols="12" md="4" class="mb-1">
              <div class="text-muted mb-25">Nettó összesen</div>
              <div class="font-weight-bold">{{ money(preparationPlan.totals.net_total) }}</div>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BFormInput,
  BProgress,
  BRow,
  BTable,
} from 'bootstrap-vue'
import axios from 'axios'
import Loading from '@/components/loading/Loading.vue'

export default {
  components: {
    BButton,
    BCard,
    BCol,
    BFormInput,
    BProgress,
    BRow,
    BTable,
    Loading,
  },
  data() {
    return {
      loading: false,
      period: '',
      projectData: null,
      varianceFields: [
        { key: 'variance_key', label: 'Mutató' },
        { key: 'planned_value', label: 'Tervezett összeg' },
        { key: 'actual_value', label: 'Tényleges összeg' },
        { key: 'variance_value', label: 'Eltérés' },
        { key: 'variance_percent', label: 'Eltérés %' },
        { key: 'severity', label: 'Súlyosság' },
      ],
      preparationPlanFields: [
        { key: 'menu_title', label: 'Menü' },
        { key: 'material_total', label: 'Anyag' },
        { key: 'fee_total', label: 'Díj' },
        { key: 'net_total', label: 'Nettó' },
      ],
      sourceFields: [
        { key: 'kpi', label: 'KPI' },
        { key: 'short', label: 'Forrás' },
        { key: 'description', label: 'Leírás' },
      ],
    }
  },
  computed: {
    sourceSummaryRows() {
      const summary = this.projectData?.sources?.summary || {}
      return Object.keys(summary).map(key => ({
        key,
        kpi: summary[key]?.label || key,
        short: summary[key]?.short || '-',
        description: summary[key]?.description || '-',
      }))
    },
    preparationPlan() {
      const fallback = {
        rows: [],
        totals: {
          material_total: 0,
          fee_total: 0,
          net_total: 0,
        },
        has_data: false,
      }
      const plan = this.projectData?.preparation_plan
      if (!plan || typeof plan !== 'object') return fallback
      return {
        rows: Array.isArray(plan.rows) ? plan.rows : [],
        totals: {
          material_total: Number(plan?.totals?.material_total || 0),
          fee_total: Number(plan?.totals?.fee_total || 0),
          net_total: Number(plan?.totals?.net_total || 0),
        },
        has_data: !!plan.has_data,
      }
    },
    timelineProgress() {
      const project = this.projectData ? this.projectData.project : null
      if (!project) return 0

      const start = this.parseDate(project.start_date)
      const end = this.parseDate(project.end_date)
      if (!start || !end) return 0

      const now = new Date()
      if (now <= start) return 0
      if (now >= end) return 100

      const total = end.getTime() - start.getTime()
      if (total <= 0) return 0

      const elapsed = now.getTime() - start.getTime()
      return Math.max(0, Math.min(100, Math.round((elapsed / total) * 100)))
    },
    timelineProgressLabel() {
      const project = this.projectData ? this.projectData.project : null
      const start = project ? this.parseDate(project.start_date) : null
      const end = project ? this.parseDate(project.end_date) : null

      if (!start || !end) return 'N/A'
      return `${this.timelineProgress}%`
    },
  },
  created() {
    this.loadProjectDetails()
  },
  methods: {
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }
    },
    loadProjectDetails() {
      const { projectId } = this.$route.params
      if (!projectId) return

      this.loading = true
      axios.get(`controlling/project/${projectId}?period=${this.period}`, { headers: this.authHeaders() })
        .then(response => {
          this.projectData = response.data || null
        }).catch(() => {
          this.projectData = null
        }).finally(() => {
          this.loading = false
        })
    },
    goBack() {
      this.$router.push({
        name: 'monolit.controlling.dashboard',
        query: { period: this.period },
      })
    },
    varianceLabel(key) {
      if (key === 'revenue') return 'Bevétel'
      if (key === 'cost') return 'Költség'
      if (key === 'margin') return 'Nyereség'
      return key || '-'
    },
    parseDate(value) {
      if (!value || typeof value !== 'string') return null

      const direct = new Date(value)
      if (!Number.isNaN(direct.getTime())) return direct

      const hu = value.match(/^(\d{4})\.(\d{2})\.(\d{2})$/)
      if (hu) {
        const parsed = new Date(`${hu[1]}-${hu[2]}-${hu[3]}`)
        return Number.isNaN(parsed.getTime()) ? null : parsed
      }

      return null
    },
    formatDate(value) {
      const date = this.parseDate(value)
      if (!date) return '-'

      return date.toLocaleDateString('hu-HU')
    },
    money(value) {
      const amount = Number(value || 0)
      return `${amount.toLocaleString('hu-HU', { maximumFractionDigits: 0 })} Ft`
    },
  },
}
</script>
